import { useCallback, useEffect, useState, useMemo } from "react";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { R_CHART_TYPES } from "../const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
// import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  getRajasthanMonthlySurplusDeficit,
  getRajasthanDailySurplusDeficit,
} from "../../../Rest_api/DemoRestApi";
import { formatDateForAPI } from "../const";
import { MetricChipHead } from "../../../components/Metrics";
import { getRajasthanSurplusMetrics } from "../../Rajasthan/Utility/RestApi";
import { SurplusDeficitData } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
const X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

// Helper function to parse month-year string back to comparable values
function parseMonthYear(monthYearStr: string | number | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) {
    return { month: 0, year: 0 };
  }

  if (typeof monthYearStr === "number") {
    return { month: monthYearStr, year: 0 };
  }

  if (typeof monthYearStr === "string") {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const parts = monthYearStr.split("-");
    if (parts.length !== 2) {
      return { month: 0, year: 0 };
    }
    const [monthStr, yearStr] = parts;
    const month = monthNames.indexOf(monthStr) + 1;
    const year = 2000 + parseInt(yearStr);
    return { month, year };
  }

  return { month: 0, year: 0 };
}

const GEN_BY = [
  {
    label: "Cumulative",
    keys: [
      { key: "drawal", label: "Drawal", stackId: "drawal" },
      { key: "total", label: "Total Gen. + Proc.", stackId: "gen" },
      { key: "deficit", label: "Surplus/Deficit", stackId: "deficit" },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
    ],
  },
  {
    label: "Comparative",
    keys: [
      { key: "drawal", label: "Drawal", stackId: "drawal" },
      { key: "total", label: "Total Gen. + Proc.", stackId: "gen" },
      { key: "deficit", label: "Surplus/Deficit", stackId: "deficit" },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
    ],
  },
];

const DISCOM = [
  { label: "Discom 1", key: "DISCOM1" },
  { label: "Discom 2", key: "DISCOM2" },
  { label: "Discom 3", key: "DISCOM3" },
];

interface RajasthanForecastPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  genGroupBy: any;
  onGenGroupByChange: (index: number) => void;
  metrics: SurplusDeficitData | null;
}

export default function RajasthanForecastPeakAnalysis({
  onXAxisModeChange,
  selectedDiscom,
  onDiscomChange,
  genGroupBy,
  onGenGroupByChange,
  metrics,
}: RajasthanForecastPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  const _handlefetchData = useCallback(async () => {
    setLoading(true);

    try {
      if (DefaultDates[activeTab].defaultRangeDate === undefined) {
        return;
      }
      const dates = DefaultDates[activeTab].defaultRangeDate;

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);
      let allData: any[] = [];

      if (selectedXaxis.key === "month") {
        const monthlyData = await getRajasthanMonthlySurplusDeficit(
          startDateStr,
          endDateStr,
        );

        if (Array.isArray(monthlyData)) {
          const formattedData = monthlyData.map((item) => {
            const monthNames = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];
            const yearShort = item.year.toString().slice(-2);
            const monthFormatted = `${monthNames[item.month - 1]}-${yearShort}`;

            return {
              ...item,
              month: monthFormatted,
              monthNumber: item.month,
            };
          });

          allData = formattedData;
        }
      } else {
        const dailyData = await getRajasthanDailySurplusDeficit(
          startDateStr,
          endDateStr,
        );

        if (Array.isArray(dailyData)) {
          allData = dailyData.map((item) => {
            const morningData =
              item.formatted_data?.find(
                (d: any) => d.time_period === "Morning",
              ) || {};
            const eveningData =
              item.formatted_data?.find(
                (d: any) => d.time_period === "Evening",
              ) || {};

            return {
              ...item,
              date: item.date,
              peak_drawal_mw: item.daily_peak_drawal_mw,
              peak_total_gen_proc_mw: item.daily_peak_total_gen_proc_mw,
              peak_surplus_deficit_mw: item.daily_peak_surplus_deficit_mw,
              peak_surplus_deficit_pct: item.daily_peak_surplus_deficit_pct,
              morning_peak_drawal_mw: morningData.max_drawal,
              morning_peak_total_gen_proc_mw: morningData.max_total_gen_proc,
              morning_peak_surplus_deficit_mw: morningData.max_surplus_deficit,
              morning_peak_surplus_deficit_pct:
                morningData.max_surplus_deficit_pct,
              evening_peak_drawal_mw: eveningData.max_drawal,
              evening_peak_total_gen_proc_mw: eveningData.max_total_gen_proc,
              evening_peak_surplus_deficit_mw: eveningData.max_surplus_deficit,
              evening_peak_surplus_deficit_pct:
                eveningData.max_surplus_deficit_pct,
            };
          });
        }
      }

      setData(allData);

      if (selectedXaxis.key === "date") {
        setUniqueDate(
          Array.from(
            new Set(
              allData
                .map((item: any) => item.date)
                .filter((date: any) => date !== undefined)
                .sort(
                  (a: string, b: string) =>
                    new Date(a).getTime() - new Date(b).getTime(),
                ),
            ),
          ),
        );
      } else {
        const uniqueMonths = Array.from(
          new Set(
            allData
              .map((item: any) => item.month)
              .filter((month: any) => month !== undefined),
          ),
        ).sort((a: string, b: string) => {
          const aMonthYear = parseMonthYear(a);
          const bMonthYear = parseMonthYear(b);
          if (aMonthYear.year !== bMonthYear.year) {
            return aMonthYear.year - bMonthYear.year;
          }
          return aMonthYear.month - bMonthYear.month;
        });
        setUniqueDate(uniqueMonths);
      }
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    if (data.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = data;

    if (selectedDate.length > 0) {
      if (selectedXaxis.key === "date") {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((d) => d.value).includes(item.date),
        );
      } else {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((d) => d.value).includes(item.month),
        );
      }
    }

    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "date") {
        if (!a.date || !b.date) return 0;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      } else {
        if (!a.month || !b.month) return 0;
        const aVal = parseMonthYear(a.month);
        const bVal = parseMonthYear(b.month);
        if (aVal.year !== bVal.year) return aVal.year - bVal.year;
        return aVal.month - bVal.month;
      }
    });

    const activeGenBy =
      GEN_BY.find((g) => g.label === genGroupBy.label) || GEN_BY[0];
    const metricsToProcess = activeGenBy.keys.map((k) => k.key);

    const processedChartData = filteredData.map((item) => {
      const baseData: any = {
        ...item,
        month: item.month,
        year: item.year,
        date: item.date,
      };

      if (metricsToProcess.includes("drawal")) {
        baseData[`drawal_peak`] = item.peak_drawal_mw;
        baseData[`drawal_morning_peak`] = item.morning_peak_drawal_mw;
        baseData[`drawal_evening_peak`] = item.evening_peak_drawal_mw;
      }

      if (metricsToProcess.includes("deficit")) {
        baseData[`deficit_peak`] = item.peak_surplus_deficit_mw;
        baseData[`deficit_morning_peak`] = item.morning_peak_surplus_deficit_mw;
        baseData[`deficit_evening_peak`] = item.evening_peak_surplus_deficit_mw;
      }

      if (metricsToProcess.includes("total")) {
        baseData[`total_peak`] = item.peak_total_gen_proc_mw;
        baseData[`total_morning_peak`] = item.morning_peak_total_gen_proc_mw;
        baseData[`total_evening_peak`] = item.evening_peak_total_gen_proc_mw;
      }

      if (metricsToProcess.includes("deficit_percent")) {
        baseData[`deficit_percent_peak`] = item.peak_surplus_deficit_pct;
        baseData[`deficit_percent_peak_date`] =
          item.date_of_peak_surplus_deficit_pct;
        baseData[`deficit_percent_peak_block`] =
          item.block_of_peak_surplus_deficit_pct;
        baseData[`deficit_percent_morning_peak`] =
          item.morning_peak_surplus_deficit_pct;
        baseData[`deficit_percent_morning_peak_date`] =
          item.date_of_morning_peak_surplus_deficit_pct;
        baseData[`deficit_percent_morning_peak_block`] =
          item.block_of_morning_peak_surplus_deficit_pct;
        baseData[`deficit_percent_evening_peak`] =
          item.evening_peak_surplus_deficit_pct;
        baseData[`deficit_percent_evening_peak_date`] =
          item.date_of_evening_peak_surplus_deficit_pct;
        baseData[`deficit_percent_evening_peak_block`] =
          item.block_of_evening_peak_surplus_deficit_pct;
      }

      return baseData;
    });

    let aggregationKeys: string[] = [];
    metricsToProcess.forEach((key) => {
      aggregationKeys.push(`${key}_peak`);
      aggregationKeys.push(`${key}_morning_peak`);
      aggregationKeys.push(`${key}_evening_peak`);
    });

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: [],
      volumeKey: "",
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: processedChartData,
    };

    const cData = FormatChartData(par);

    setTableData(cData.tableData || []);
    setChartData(processedChartData);
    setChartKeys(metricsToProcess);
  }, [data, selectedXaxis, selectedDate, genGroupBy]);

  const legends = useMemo(() => {
    const peakTypes = [
      { field: "peak", displayName: "Day Peak" },
      { field: "morning_peak", displayName: "Morning Peak" },
      { field: "evening_peak", displayName: "Evening Peak" },
    ];

    const legendsArray: any[] = [];

    const activeGenBy =
      GEN_BY.find((g) => g.label === genGroupBy.label) || GEN_BY[0];

    let genCount = 1;
    activeGenBy.keys.forEach((metricConfig) => {
      const metricKey = metricConfig.key;
      const metricLabel = metricConfig.label;

      if (!chartKeys.includes(metricKey)) return;

      peakTypes.forEach((peak) => {
        // --- DEMO MASKING: mask gen sources as Power plant N ---
        const displayName =
          metricConfig.stackId === "gen"
            ? `Power plant ${genCount}`
            : metricLabel;
        legendsArray.push({
          dataKey: `${metricKey}_${peak.field}`,
          name: `${displayName} ${peak.displayName}`,
          legendColor: undefined,
          stroke: undefined,
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? peak.field : undefined,
        });
      });
    });

    return legendsArray;
  }, [chartKeys, genGroupBy, selectedChartType]);

  function summaryExtraKeys() {
    if (selectedXaxis.key === "date") {
      return ["date"];
    } else {
      return ["month", "year"];
    }
  }

  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month", "Year"];

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: "MW",
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Forecast Surplus Deficit Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
              setSelectedDate([]);
            }}
          />
          <FilterMenu
            title="Generation By"
            items={GEN_BY.map((item) => item.label)}
            selectedItems={[genGroupBy.label]}
            onSelect={(item, index) => onGenGroupByChange(index)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={onDiscomChange}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="flex flex-wrap gap-4 my-2 justify-center">
          <MetricChipHead
            icon={<PeakIcon width={20} height={19} />}
            title="Surplus/Deficit"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]
                    ?.value ?? undefined,
                unit:
                  metrics?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]
                    ?.unit ?? "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]
                    ?.value ?? undefined,
                unit:
                  metrics?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]
                    ?.unit ?? "",
              },
            ]}
          />
          <MetricChipHead
            icon={<AvgIcon width={20} height={19} />}
            title="Total Generation"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.generation_metrics?.["Max Generation (MW)"]?.value ??
                  undefined,
                unit:
                  metrics?.generation_metrics?.["Max Generation (MW)"]?.unit ??
                  "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.generation_metrics?.["Min Generation (MW)"]?.value ??
                  undefined,
                unit:
                  metrics?.generation_metrics?.["Min Generation (MW)"]?.unit ??
                  "",
              },
            ]}
          />
          <MetricChipHead
            icon={<MinIcon width={20} height={19} />}
            title="Load"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.load_metrics?.["Max Load (MW)"]?.value ?? undefined,
                unit: metrics?.load_metrics?.["Max Load (MW)"]?.unit ?? "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.load_metrics?.["Min Load (MW)"]?.value ?? undefined,
                unit: metrics?.load_metrics?.["Min Load (MW)"]?.unit ?? "",
              },
            ]}
          />
        </div>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Forecast Surplus Peak"
              subtitle="Fetching Demo State forecast surplus peak data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading">
                  Surplus/Deficit Peak Analysis (MW)
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart") {
                        downloadChartTableCSV(tablePar);
                      } else if (dataTab.label === "Summary") {
                        downloadSummaryCSV(tablePar);
                      } else if (dataTab.label === "Table") {
                        downloadChartTableCSV(tablePar);
                      }
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>
              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit="MW"
                  yAxisLabel="MW"
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={selectedXaxis.values[0]}
                  secondXDataKey={undefined}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={
                <p>Forecast of Total Drawal and Generation + Procurement</p>
              }
              text="1. Surplus/Deficit = Total Gen. + Proc. - Total Drawal
2. MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
